import { CompanyUserProductEntitlements } from '@treasury/domain/company/user';
import { TmBaseComponent } from '@treasury/presentation';
import '@treasury/presentation/components/jh-ui/tm-checkbox';
import { html, nothing } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import { Claim } from '../claim.vm';
import {
    ClaimGroup,
    getClaimDisplayLabel,
    groupClaimsByBaseName,
} from '../utils/claim-grouping.util';

type FormControlEvent<T> = CustomEvent<{ value: T; isValid: boolean }>;

@customElement('tm-company-user-account-entitlement-defaults')
export class CompanyUserAccountEntitlementDefaults extends TmBaseComponent {
    @property()
    public entitlements?: CompanyUserProductEntitlements;

    @state()
    private claims: Claim[] = [];

    public render() {
        if (this.claims.length < 1) {
            return nothing;
        }
        return html` <div class="text-md font-bold">Set entitlement defaults</div>
            <div class="text-sm text-gray-500 mb-5">
                Entitlement defaults apply to all accounts but can be overridden per account.
            </div>
            ${this.renderProductEntitlements()}`;
    }

    public connectedCallback() {
        super.connectedCallback();
        this.claims = this.entitlements?.accountProductClaims || [];
        this.checkIfAllAccountClaimsAreSelected();
    }

    private checkIfAllAccountClaimsAreSelected() {
        this.claims.forEach(claim => {
            const accountClaims = this.entitlements?.getAccountClaimsByClaimId(claim.id);
            if (accountClaims?.every(accountClaim => accountClaim.hasUserClaim)) {
                claim.allAccountsSelected = true;
            }
        });
    }

    private renderProductEntitlements() {
        const groupedClaims = groupClaimsByBaseName(this.claims);
        return html`<div class="flex flex-row gap-x-32">
            ${groupedClaims.map(group => this.renderClaimGroup(group))}
        </div>`;
    }

    private renderClaimGroup(group: ClaimGroup<Claim>) {
        const isAllSelected = group.claims.every(child => child.claim.allAccountsSelected);
        return html`<div>
            <tm-wc-checkbox
                class="ext-lg font-semibold mb-3"
                @value-changed=${(e: FormControlEvent<boolean>) => this.handleSelectAll(e, group)}
                .initial=${isAllSelected}
                .label=${group.baseName}
            ></tm-wc-checkbox>
            <div class="flex flex-col gap-2">
                ${group.claims.map(groupedClaim =>
                    this.renderClaim(groupedClaim.claim, groupedClaim.action)
                )}
            </div>
        </div>`;
    }

    private renderClaim(claim: Claim, action?: string) {
        const label = getClaimDisplayLabel(claim, action);
        return html`<div class="min-w-0 flex-shrink-0">
            <tm-wc-checkbox
                class="ml-10"
                .label=${label}
                .initial=${claim.allAccountsSelected}
                @value-changed=${(e: FormControlEvent<boolean>) => this.handleClaimChange(e, claim)}
            ></tm-wc-checkbox>
        </div>`;
    }

    private handleSelectAll(e: FormControlEvent<boolean>, group: ClaimGroup<Claim>) {
        const { value } = e.detail;
        group.claims.forEach(child => {
            child.claim.allAccountsSelected = value;
        });
        this.requestUpdate();
    }

    private handleClaimChange(e: FormControlEvent<boolean>, claim: Claim) {
        const { value } = e.detail;
        claim.allAccountsSelected = value;
        this.requestUpdate();
    }
}
